/**
 * Material Form Page
 *
 * Page for creating and editing materials
 * Features:
 * - Create new material
 * - Edit existing material
 * - Category selection
 * - Initial price setting
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  Save,
  Loader2,
  Boxes,
  AlertCircle,
} from 'lucide-react';
import Layout from '../components/Layout';
import authService from '../services/authService';
import materialService from '../services/materialService';

function MaterialFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    product_id: '',
    name: '',
    material_category: '',
    unit: '个',
    diameter: '',
    specification: '',
    default_unit_cost: '',
    tax_rate: '0.13',
    notes: '',
  });

  // Load user info
  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
  }, []);

  // Load material data for edit mode
  const loadMaterial = useCallback(async () => {
    if (!isEditMode) return;

    try {
      setLoading(true);
      setError(null);

      const material = await materialService.getMaterial(id);
      setFormData({
        product_id: material.product_id || '',
        name: material.name || '',
        material_category: material.material_category || '',
        unit: material.unit || '个',
        diameter: material.diameter?.toString() || '',
        specification: material.specification || '',
        default_unit_cost: material.default_unit_cost?.toString() || '',
        tax_rate: '0.13',
        notes: material.notes || '',
      });
    } catch (err) {
      console.error('Failed to load material:', err);
      setError('加载物料数据失败，请重试。');
    } finally {
      setLoading(false);
    }
  }, [id, isEditMode]);

  useEffect(() => {
    loadMaterial();
  }, [loadMaterial]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.product_id.trim()) {
      errors.product_id = '请输入物料编号';
    }

    if (!formData.name.trim()) {
      errors.name = '请输入物料名称';
    }

    if (formData.diameter && isNaN(parseFloat(formData.diameter))) {
      errors.diameter = '直径必须是数字';
    }

    if (formData.default_unit_cost && isNaN(parseFloat(formData.default_unit_cost))) {
      errors.default_unit_cost = '价格必须是数字';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const submitData = {
        product_id: formData.product_id.trim(),
        name: formData.name.trim(),
        material_category: formData.material_category,
        unit: formData.unit,
        specification: formData.specification.trim(),
        notes: formData.notes.trim(),
      };

      if (formData.diameter) {
        submitData.diameter = parseFloat(formData.diameter);
      }

      if (formData.default_unit_cost) {
        submitData.default_unit_cost = parseFloat(formData.default_unit_cost);
        submitData.tax_rate = parseFloat(formData.tax_rate);
      }

      if (isEditMode) {
        await materialService.updateMaterial(id, submitData);
      } else {
        await materialService.createMaterial(submitData);
      }

      navigate('/materials');
    } catch (err) {
      console.error('Failed to save material:', err);
      const errorMessage = err.response?.data?.error || err.message;
      setError(`保存物料失败: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/materials');
  };

  // Check permissions
  // Check permissions
  const canEdit = user && ['admin', 'warehouse_manager', 'engineer'].includes(user.role);

  if (loading) {
    return (
      <Layout activeTab="materials">
        <div className="container mx-auto py-6">
          <div className="flex justify-center items-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!canEdit) {
    return (
      <Layout activeTab="materials">
        <div className="container mx-auto py-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>您没有权限执行此操作</AlertDescription>
          </Alert>
          <Button onClick={handleBack} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            返回
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout activeTab="materials">
      <div className="container mx-auto py-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">
                {isEditMode ? '编辑物料' : '新建物料'}
              </h1>
              <p className="text-muted-foreground">
                {isEditMode ? '修改物料信息' : '创建新的原材料'}
              </p>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Boxes className="h-5 w-5" />
                物料信息
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="product_id">
                    物料编号 <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="product_id"
                    name="product_id"
                    placeholder="例如: BAR-6MM"
                    value={formData.product_id}
                    onChange={handleInputChange}
                    disabled={isEditMode}
                    className={validationErrors.product_id ? 'border-destructive' : ''}
                  />
                  {validationErrors.product_id && (
                    <p className="text-sm text-destructive">{validationErrors.product_id}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">
                    物料名称 <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="例如: 6mm硬质合金棒料"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={validationErrors.name ? 'border-destructive' : ''}
                  />
                  {validationErrors.name && (
                    <p className="text-sm text-destructive">{validationErrors.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="material_category">物料类别</Label>
                  <Select
                    value={formData.material_category}
                    onValueChange={(value) => handleSelectChange('material_category', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="选择类别" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="棒料">棒料</SelectItem>
                      <SelectItem value="紧固件">紧固件</SelectItem>
                      <SelectItem value="耗材">耗材</SelectItem>
                      <SelectItem value="其他">其他</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="unit">单位</Label>
                  <Select
                    value={formData.unit}
                    onValueChange={(value) => handleSelectChange('unit', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="选择单位" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="个">个</SelectItem>
                      <SelectItem value="根">根</SelectItem>
                      <SelectItem value="件">件</SelectItem>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="m">m</SelectItem>
                      <SelectItem value="盒">盒</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {(formData.material_category === '棒料' || formData.diameter) && (
                  <div className="space-y-2">
                    <Label htmlFor="diameter">直径 (mm)</Label>
                    <Input
                      id="diameter"
                      name="diameter"
                      type="number"
                      step="0.1"
                      placeholder="例如: 6.0"
                      value={formData.diameter}
                      onChange={handleInputChange}
                      className={validationErrors.diameter ? 'border-destructive' : ''}
                    />
                    {validationErrors.diameter && (
                      <p className="text-sm text-destructive">{validationErrors.diameter}</p>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="specification">规格</Label>
                  <Input
                    id="specification"
                    name="specification"
                    placeholder="物料规格说明"
                    value={formData.specification}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Price Info (only for new materials) */}
              {!isEditMode && (
                <>
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-medium mb-4">初始价格（可选）</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="default_unit_cost">含税单价</Label>
                        <Input
                          id="default_unit_cost"
                          name="default_unit_cost"
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={formData.default_unit_cost}
                          onChange={handleInputChange}
                          className={validationErrors.default_unit_cost ? 'border-destructive' : ''}
                        />
                        {validationErrors.default_unit_cost && (
                          <p className="text-sm text-destructive">{validationErrors.default_unit_cost}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="tax_rate">税率</Label>
                        <Select
                          value={formData.tax_rate}
                          onValueChange={(value) => handleSelectChange('tax_rate', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="选择税率" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0.13">13%</SelectItem>
                            <SelectItem value="0.09">9%</SelectItem>
                            <SelectItem value="0.06">6%</SelectItem>
                            <SelectItem value="0">0%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">备注</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  placeholder="可选的备注信息"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={3}
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-4 pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleBack}>
                  取消
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  <Save className="h-4 w-4 mr-2" />
                  {isEditMode ? '保存' : '创建'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </Layout>
  );
}

export default MaterialFormPage;
